import { ApiError, HTTP_STATUS } from "@sidegigllc/api";
import type { UserResponse, UserRetrieveResponse } from "@sidegigllc/api/client";

/** Auth data (`/users/me/`, manager profile) is refetched imperatively on auth events, not by time. */
export const AUTH_STALE_TIME = Number.POSITIVE_INFINITY;

const expectedErrors = new Set<number>([HTTP_STATUS.UNAUTHORIZED, HTTP_STATUS.NOT_FOUND]);

// `/users/me/` always resolves to the authenticated `User`; the response type is a union only
// because a numeric id (e.g. admin) can return a gigster shape.
export const selectAuthUser = (data: UserRetrieveResponse) => data as UserResponse;

export const isExpectedError = (error: unknown) => {
  const apiError = ApiError.fromThrown(error);
  const status = apiError.httpStatus;
  return status !== undefined && expectedErrors.has(status);
};

type OnboardingGateInput = {
  /** Gates stay shut until the queries behind them settle. */
  loaded: boolean;
  appMode?: string;
  managerProfileId?: number;
  profileHasLocations?: boolean;
  /** `undefined` while the locations query is unresolved or failed — never read that as zero. */
  activeLocationCount?: number;
  isGigproAdmin: boolean;
};

type OnboardingGates = {
  hasLocations: boolean;
  signUpRequired: boolean;
  locationRequired: boolean;
};

/** Decides whether the user still owes us onboarding, and if so which kind. */
export const deriveOnboardingGates = ({
  loaded,
  appMode,
  managerProfileId,
  profileHasLocations,
  activeLocationCount,
  isGigproAdmin,
}: OnboardingGateInput): OnboardingGates => {
  // Each source has a blind spot: the list omits DENIED/FINANCE locations, the profile 404s away.
  const hasLocations = (activeLocationCount ?? 0) > 0 || !!profileHasLocations;

  // An unknown count must not prompt — a failed request would otherwise look like a new user.
  const locationsKnown = loaded && activeLocationCount !== undefined;

  // Locations prove onboarding; `app_mode` is often blank, which would gate forever.
  const signUpRequired = locationsKnown && !hasLocations && !(appMode && managerProfileId);

  return {
    hasLocations,
    signUpRequired,
    // Onboarded, but nothing to manage yet.
    locationRequired: locationsKnown && !signUpRequired && !hasLocations && !isGigproAdmin,
  };
};
